import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { casesAPI, documentsAPI, referralAPI, poaAPI, messagesAPI } from '../utils/api';
import { BlinkingDot } from '../components/UnreadBadge';
import { Link } from 'react-router-dom';
import { DollarSign, Clock, TrendingUp, FileText, LogOut, Upload, X, UserPlus, Gift, Plus, CheckCircle, PenTool, Scale } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import ReferralAgreement from './ReferralAgreement';
import ReferMerchant from './ReferMerchant';
import DocumentUploadModal from './DocumentUploadModal';
import SignedDocuments from '../components/SignedDocuments';
import LimitedPOAForm from '../components/LimitedPOAForm';
import ClientKanbanBoard from '../components/ClientKanbanBoard';

const ClientDashboard = () => {
  const { user, logout } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showReferralAgreement, setShowReferralAgreement] = useState(false);
  const [showReferMerchant, setShowReferMerchant] = useState(false);
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  const [activeTab, setActiveTab] = useState('cases');
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [showPOAForm, setShowPOAForm] = useState(false);
  const [selectedCaseForPOA, setSelectedCaseForPOA] = useState(null);
  const [casePOAStatus, setCasePOAStatus] = useState({});
  const [unreadByCase, setUnreadByCase] = useState({});

  useEffect(() => {
    loadCases();
    loadReferrals();
    loadUnreadMessages();
  }, []);

  const loadUnreadMessages = async () => {
    try {
      const response = await messagesAPI.getUnreadByCase();
      setUnreadByCase(response.data || {});
    } catch (error) {
      console.error('Failed to load unread messages:', error);
    }
  };

  // Load POA status for each case
  useEffect(() => {
    const loadPOAStatuses = async () => {
      const statuses = {};
      for (const caseItem of cases) {
        try {
          const response = await poaAPI.getPOAForCase(caseItem.id);
          if (response.data) {
            statuses[caseItem.id] = response.data.status;
          }
        } catch (error) {
          // No POA for this case
        }
      }
      setCasePOAStatus(statuses);
    };
    if (cases.length > 0) {
      loadPOAStatuses();
    }
  }, [cases]);

  const handleOpenPOAForm = (caseItem) => {
    setSelectedCaseForPOA(caseItem);
    setShowPOAForm(true);
  };

  const handlePOASuccess = () => {
    loadCases();
    // Refresh POA status
    setCasePOAStatus(prev => ({
      ...prev,
      [selectedCaseForPOA?.id]: 'pending_admin'
    }));
  };

  const loadCases = async () => {
    try {
      const response = await casesAPI.getCases();
      setCases(response.data);
    } catch (error) {
      console.error('Failed to load cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReferrals = async () => {
    try {
      const [merchantsRes, statsRes] = await Promise.all([
        referralAPI.getMerchants(),
        referralAPI.getDashboardStats()
      ]);
      setReferrals(merchantsRes.data);
      setReferralStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load referrals:', error);
    }
  };

  // Combine cases with referral merchant info for display
  const getCombinedCases = () => {
    // Add referral info to cases that are from referrals
    const casesWithReferralInfo = cases.map(c => {
      // Find matching referral merchant if this is a referral case
      const matchingReferral = referrals.find(r => 
        r.merchant_company_name === c.company_name || 
        r.case_id === c.id
      );
      return {
        ...c,
        referral_info: matchingReferral || null,
        has_referral_agreement: !!matchingReferral
      };
    });

    // Create case-like objects from referral merchants that don't have a case yet
    const referralOnlyCases = referrals
      .filter(r => !cases.some(c => 
        c.company_name === r.merchant_company_name || 
        c.id === r.case_id
      ))
      .map(r => ({
        id: r.id,
        company_name: r.merchant_company_name || 'Referral Case',
        processor_name: r.processor_name || 'N/A',
        frozen_amount: r.held_reserves_amount || r.total_held_reserves || 0,
        freeze_date: r.submitted_date || r.created_at,
        status: r.status === 'accepted' ? 'in_progress' : r.status === 'paid' ? 'resolved' : 'new',
        estimated_release_date: null,
        is_referral: true,
        is_referral_only: true,
        referral_info: r,
        has_referral_agreement: true
      }));

    return [...casesWithReferralInfo, ...referralOnlyCases];
  };

  const combinedCases = getCombinedCases();

  const handleOpenUploadModal = (caseId) => {
    setSelectedCaseId(caseId);
    setUploadModalOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('case_id', selectedCaseId);
    formData.append('document_type', 'merchant_processing');

    try {
      await documentsAPI.upload(formData);
      toast.success('Document uploaded successfully!');
      setUploadModalOpen(false);
      loadCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-badge status-pending',
      in_progress: 'status-badge status-in-progress',
      resolved: 'status-badge status-resolved',
      closed: 'status-badge status-closed'
    };
    return statusClasses[status] || statusClasses.pending;
  };

  const totalFrozen = combinedCases.reduce((sum, c) => sum + (c.frozen_amount || 0), 0);
  const activeCases = combinedCases.filter(c => c.status !== 'closed' && c.status !== 'resolved').length;
  const resolvedCases = combinedCases.filter(c => c.status === 'resolved').length;

  return (
    <div className="app-layout min-h-screen">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="reserverelease.AI" className="h-12 sm:h-14 object-contain" />
            <div className="flex flex-col">
              <span className="text-lg sm:text-xl md:text-2xl font-bold">
                <span className="bg-gradient-to-r from-purple-600 via-cyan-600 to-purple-600 bg-clip-text text-transparent">
                  ReserveRelease
                </span>
                <span className="text-slate-900">.AI</span>
              </span>
              <span className="text-xs text-slate-500 hidden sm:block">Client Portal</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowReferralAgreement(true)}
              className="flashing-upload-btn bg-gradient-to-r from-purple-600 to-cyan-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:shadow-lg transition-all text-sm"
              data-testid="referral-agreement-btn"
            >
              <Gift size={18} />
              Referral Agreement
            </button>
            <button
              onClick={() => setShowCreateCaseModal(true)}
              className="bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:shadow-lg transition-all text-sm"
              data-testid="create-case-btn"
            >
              <Plus size={18} />
              New Case
            </button>
            <Link 
              to="/settings" 
              className="text-slate-600 hover:text-purple-600 transition-colors font-medium"
              data-testid="settings-link"
            >
              {user?.full_name}
            </Link>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Dashboard Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-slate-600">Track your reserve recovery progress</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('cases')}
            className={`pb-3 px-4 font-semibold transition-colors ${
              activeTab === 'cases'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="cases-tab"
          >
            My Cases
          </button>
          <button
            onClick={() => setActiveTab('referrals')}
            className={`pb-3 px-4 font-semibold transition-colors ${
              activeTab === 'referrals'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="referrals-tab"
          >
            My Referrals
            {referralStats && referralStats.total_referrals > 0 && (
              <span className="ml-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                {referralStats.total_referrals}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`pb-3 px-4 font-semibold transition-colors ${
              activeTab === 'tasks'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="tasks-tab"
          >
            My Tasks
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`pb-3 px-4 font-semibold transition-colors flex items-center gap-2 ${
              activeTab === 'documents'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="documents-tab"
          >
            <PenTool size={16} />
            Signed Documents
          </button>
          <button
            onClick={() => setActiveTab('poa')}
            className={`pb-3 px-4 font-semibold transition-colors flex items-center gap-2 ${
              activeTab === 'poa'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="poa-tab"
          >
            <Scale size={16} />
            Power of Attorney
          </button>
        </div>

        {/* Stats Grid - Cases Tab */}
        {activeTab === 'cases' && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <DollarSign className="text-purple-600" size={24} />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900" data-testid="total-frozen-amount">
              ${totalFrozen.toLocaleString()}
            </div>
            <div className="text-sm text-slate-600 mt-1">Total Frozen</div>
          </div>

          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="text-blue-600" size={24} />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900" data-testid="active-cases-count">{activeCases}</div>
            <div className="text-sm text-slate-600 mt-1">Active Cases</div>
          </div>

          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="text-green-600" size={24} />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900" data-testid="resolved-cases-count">{resolvedCases}</div>
            <div className="text-sm text-slate-600 mt-1">Resolved</div>
          </div>

          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="text-amber-600" size={24} />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900">{cases.length}</div>
            <div className="text-sm text-slate-600 mt-1">Total Cases</div>
          </div>
          </div>
        )}

        {/* Instruction Card - Cases Tab */}
        {activeTab === 'cases' && (
          <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                <FileText className="text-white" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 mb-2">How to Get Started</h3>
                <div className="space-y-2 text-sm text-slate-700">
                  <p><strong>Step 1:</strong> View your existing cases below. Each case tracks frozen reserves with your merchant processor.</p>
                  <p><strong>Step 2:</strong> Click &ldquo;MPC Review&rdquo; to upload your Merchant Processing Contract for AI analysis. The AI will generate a summary report with key findings.</p>
                  <p><strong>Step 3:</strong> Want to refer a merchant? Click the <span className="font-semibold text-purple-600">&ldquo;Referral Agreement&rdquo;</span> button above to earn 10% commission on recovered funds!</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cases List */}
        {activeTab === 'cases' && (
          <div className="dashboard-card">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Your Cases</h2>
          
          {loading ? (
            <div className="text-center py-12 text-slate-600">Loading cases...</div>
          ) : combinedCases.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 mb-4">No cases found</p>
              <p className="text-sm text-slate-500">Contact us to start your reserve recovery process</p>
            </div>
          ) : (
            <div className="space-y-4">
              {combinedCases.map((caseItem) => (
                <div
                  key={caseItem.id}
                  className={`border rounded-lg p-6 hover:shadow-md transition-all ${
                    caseItem.is_referral || caseItem.is_referral_only 
                      ? 'border-purple-300 bg-purple-50/30 hover:border-purple-400' 
                      : 'border-slate-200 hover:border-purple-300'
                  }`}
                  data-testid={`case-item-${caseItem.id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      {caseItem.is_referral_only ? (
                        <h3 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                          {caseItem.company_name}
                          <span className="bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                            Referral
                          </span>
                        </h3>
                      ) : (
                        <Link to={`/case/${caseItem.id}`}>
                          <h3 className="text-lg font-bold text-slate-900 mb-1 hover:text-purple-600 flex items-center gap-2">
                            {caseItem.company_name}
                            {(caseItem.is_referral || caseItem.has_referral_agreement) && (
                              <span className="bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                                Referral
                              </span>
                            )}
                            {unreadByCase[caseItem.id] > 0 && (
                              <BlinkingDot active={true} size="sm" />
                            )}
                          </h3>
                        </Link>
                      )}
                      <p className="text-sm text-slate-600">Processor: {caseItem.processor_name}</p>
                      {caseItem.referral_info && (
                        <p className="text-xs text-purple-600 mt-1">
                          Referred: {caseItem.referral_info.merchant_contact_name || caseItem.referral_info.merchant_company_name}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap justify-end">
                      <span className={getStatusBadge(caseItem.status)}>
                        {caseItem.status.replace('_', ' ')}
                      </span>
                      {/* Referral Agreement Status Badge */}
                      {caseItem.referral_info && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          caseItem.referral_info.status === 'paid' 
                            ? 'bg-green-100 text-green-700' 
                            : caseItem.referral_info.status === 'accepted' || caseItem.referral_info.status === 'recovered'
                            ? 'bg-blue-100 text-blue-700'
                            : caseItem.referral_info.status === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {caseItem.referral_info.status === 'paid' ? 'Commission Paid' : 
                           caseItem.referral_info.status === 'recovered' ? 'Funds Recovered' :
                           caseItem.referral_info.status === 'accepted' ? 'In Progress' :
                           caseItem.referral_info.status === 'rejected' ? 'Rejected' : 'Pending Review'}
                        </span>
                      )}
                      {/* POA Status Badge - only for non-referral-only cases */}
                      {!caseItem.is_referral_only && casePOAStatus[caseItem.id] && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          casePOAStatus[caseItem.id] === 'completed' 
                            ? 'bg-green-100 text-green-700' 
                            : casePOAStatus[caseItem.id] === 'pending_admin'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}>
                          {casePOAStatus[caseItem.id] === 'completed' ? 'POA Complete' : 
                           casePOAStatus[caseItem.id] === 'pending_admin' ? 'POA Pending Signature' : 
                           'POA Draft'}
                        </span>
                      )}
                      {/* POA Button - only show when not completed */}
                      {!caseItem.is_referral_only && casePOAStatus[caseItem.id] !== 'completed' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenPOAForm(caseItem);
                          }}
                          className="bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:shadow-lg transition-all"
                          data-testid={`poa-btn-${caseItem.id}`}
                        >
                          <Scale size={18} />
                          {casePOAStatus[caseItem.id] === 'pending_admin' ? 'View POA' : 'Sign POA'}
                        </button>
                      )}
                      {/* MPC Review Button (renamed from Upload Docs) */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenUploadModal(caseItem.is_referral_only ? null : caseItem.id);
                        }}
                        className={`${caseItem.status === 'resolved' ? '' : 'flashing-upload-btn'} bg-gradient-to-r from-purple-600 to-cyan-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:shadow-lg transition-all`}
                        data-testid={`upload-btn-${caseItem.id}`}
                        disabled={caseItem.is_referral_only && !caseItem.referral_info?.case_id}
                        title={caseItem.is_referral_only && !caseItem.referral_info?.case_id ? 'Case will be created when referral is accepted' : 'Upload Merchant Processing Contract for AI Review'}
                      >
                        <Upload size={18} />
                        MPC Review
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-slate-600">Frozen Amount</div>
                      <div className="font-semibold text-slate-900">${(caseItem.frozen_amount || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-slate-600">{caseItem.is_referral_only ? 'Submitted' : 'Freeze Date'}</div>
                      <div className="font-semibold text-slate-900">
                        {caseItem.freeze_date
                          ? format(new Date(caseItem.freeze_date), 'MMM dd, yyyy')
                          : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-600">{caseItem.is_referral_only ? 'Commission (10%)' : 'Est. Release'}</div>
                      <div className="font-semibold text-slate-900">
                        {caseItem.is_referral_only 
                          ? `$${((caseItem.frozen_amount || 0) * 0.1).toLocaleString()}`
                          : caseItem.estimated_release_date
                            ? format(new Date(caseItem.estimated_release_date), 'MMM dd, yyyy')
                            : 'TBD'}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-600">{caseItem.is_referral_only ? 'Referral ID' : 'Case ID'}</div>
                      <div className="font-mono text-xs text-slate-700">{caseItem.id.substring(0, 8)}...</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        )}

        {/* Instruction Card - Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                <Gift className="text-white" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Referral Program - Earn 10% Commission</h3>
                <div className="space-y-2 text-sm text-slate-700">
                  <p><strong>Step 1:</strong> Click <span className="font-semibold text-purple-600">&ldquo;Referral Agreement&rdquo;</span> button above to create your agreement (one-time setup).</p>
                  <p><strong>Step 2:</strong> Submit merchant information directly in the agreement form - include business details, contact info, bank details, and frozen amount.</p>
                  <p><strong>Step 3:</strong> We review and accept the referral within 15 business days. Track status here!</p>
                  <p><strong>Step 4:</strong> When we recover funds, you earn <strong>10% commission</strong> paid within 30 days!</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Commission Analytics - Referrals Tab */}
        {activeTab === 'referrals' && referralStats && (
          <div className="mb-8">
            {/* Commission Summary Card */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-green-600 flex items-center justify-center">
                    <DollarSign className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-green-800">
                      ${(referralStats.total_commission_earned || 0).toLocaleString()}
                    </h3>
                    <p className="text-green-700 font-medium">Total Commission Earned</p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="text-center">
                    <div className="text-xl font-bold text-slate-900">{referralStats.total_referrals || 0}</div>
                    <div className="text-xs text-slate-600">Total Referrals</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-green-600">{referralStats.accepted_referrals || 0}</div>
                    <div className="text-xs text-slate-600">Accepted</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-amber-600">{referralStats.pending_referrals || 0}</div>
                    <div className="text-xs text-slate-600">Pending</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Commission Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Potential Commission */}
              <div className="dashboard-card border-l-4 border-l-purple-500">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <TrendingUp className="text-purple-600" size={20} />
                  </div>
                  <div>
                    <div className="text-sm text-slate-600">Potential Commission</div>
                    <div className="text-xl font-bold text-purple-600">
                      ${((referralStats.total_potential_reserves || 0) * 0.1).toLocaleString()}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500">Based on ${(referralStats.total_potential_reserves || 0).toLocaleString()} in pending reserves</p>
              </div>

              {/* Pending Payout */}
              <div className="dashboard-card border-l-4 border-l-amber-500">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Clock className="text-amber-600" size={20} />
                  </div>
                  <div>
                    <div className="text-sm text-slate-600">Pending Payout</div>
                    <div className="text-xl font-bold text-amber-600">
                      ${(referralStats.commission_pending || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500">Commission awaiting payment</p>
              </div>

              {/* Paid Commission */}
              <div className="dashboard-card border-l-4 border-l-green-500">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="text-green-600" size={20} />
                  </div>
                  <div>
                    <div className="text-sm text-slate-600">Commission Paid</div>
                    <div className="text-xl font-bold text-green-600">
                      ${(referralStats.commission_paid || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500">Successfully deposited to your account</p>
              </div>
            </div>
          </div>
        )}

        {/* Referrals List */}
        {activeTab === 'referrals' && (
          <div className="dashboard-card">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Your Referrals</h2>
                <p className="text-sm text-slate-600 mt-1">Earn 10% commission on all recovered funds</p>
              </div>
              <button
                onClick={() => setShowReferralAgreement(true)}
                className="primary-button flex items-center gap-2"
                data-testid="refer-merchant-btn"
              >
                <Gift size={20} />
                Submit New Referral
              </button>
            </div>
            
            {referrals.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift className="text-purple-600" size={40} />
                </div>
                <p className="text-slate-600 mb-4">No referrals yet</p>
                <p className="text-sm text-slate-500 mb-6">Start referring merchants with frozen reserves and earn 10% commission!</p>
                <button
                  onClick={() => setShowReferMerchant(true)}
                  className="primary-button"
                >
                  Submit Your First Referral
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {referrals.map((referral) => (
                  <div
                    key={referral.id}
                    className="border border-slate-200 rounded-lg p-6 hover:border-purple-300 hover:shadow-md transition-all"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-slate-900 mb-1">{referral.merchant_contact_name}</h3>
                        <p className="text-sm text-slate-600">{referral.merchant_contact_email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`status-badge ${
                          referral.status === 'pending' ? 'status-pending' :
                          referral.status === 'accepted' ? 'status-in-progress' :
                          referral.status === 'recovered' ? 'bg-green-100 text-green-800' :
                          referral.status === 'paid' ? 'status-resolved' :
                          'bg-red-100 text-red-800'
                        } px-3 py-1 rounded-full text-xs font-semibold`}>
                          {referral.status}
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-slate-600">Held Reserves</div>
                        <div className="font-semibold text-slate-900">${referral.held_reserves_amount.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-slate-600">Submitted</div>
                        <div className="font-semibold text-slate-900">
                          {format(new Date(referral.submitted_date), 'MMM dd, yyyy')}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-600">Recovered</div>
                        <div className="font-semibold text-slate-900">
                          {referral.recovered_amount ? `$${referral.recovered_amount.toLocaleString()}` : '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-600">Your Commission</div>
                        <div className={`font-semibold ${referral.referral_fee_paid ? 'text-green-600' : 'text-slate-900'}`}>
                          {referral.referral_fee_amount ? (
                            <>
                              ${referral.referral_fee_amount.toLocaleString()}
                              {referral.referral_fee_paid && <span className="text-xs ml-1">✓ Paid</span>}
                            </>
                          ) : '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {referralStats && referralStats.commission_pending > 0 && (
              <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-semibold text-slate-900">Pending Commission Payment</div>
                    <div className="text-sm text-slate-600">Will be paid within 30 days of fund recovery</div>
                  </div>
                  <div className="text-2xl font-bold text-purple-600">
                    ${referralStats.commission_pending.toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tasks Tab - Kanban Board */}
        {activeTab === 'tasks' && (
          <div>
            <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="text-white" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Reserve Recovery Tasks</h3>
                  <p className="text-sm text-slate-700">
                    Track your progress through the reserve recovery process. Drag tasks between columns to update their status. 
                    Add comments and request admin review when you need assistance.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="dashboard-card">
              <ClientKanbanBoard cases={cases} />
            </div>
          </div>
        )}

        {/* Signed Documents Tab */}
        {activeTab === 'documents' && (
          <div>
            <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                  <PenTool className="text-white" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Signed Documents</h3>
                  <p className="text-sm text-slate-700">
                    View, download, and print all your signed agreements and contracts. Documents are securely stored and backed up to Dropbox.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="dashboard-card">
              <SignedDocuments isAdmin={false} />
            </div>
          </div>
        )}

        {/* Power of Attorney Tab */}
        {activeTab === 'poa' && (
          <div>
            <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                  <Scale className="text-white" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Power of Attorney Documents</h3>
                  <p className="text-sm text-slate-700">
                    View and manage your Limited Power of Attorney documents. Completed POAs authorize Reserve Release AI to act on your behalf for reserve recovery.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="dashboard-card">
              <POAList 
                cases={cases} 
                casePOAStatus={casePOAStatus} 
                onSignPOA={handleOpenPOAForm}
                loadCases={loadCases}
              />
            </div>
          </div>
        )}
      </div>

      {/* Referral Agreement Modal */}
      {showReferralAgreement && (
        <ReferralAgreement onClose={() => setShowReferralAgreement(false)} />
      )}

      {/* Refer Merchant Modal */}
      {showReferMerchant && (
        <ReferMerchant
          onClose={() => setShowReferMerchant(false)}
          onSuccess={loadReferrals}
        />
      )}

      {/* Document Upload Modal */}
      {uploadModalOpen && selectedCaseId && (
        <DocumentUploadModal
          caseId={selectedCaseId}
          caseInfo={cases.find(c => c.id === selectedCaseId) || {}}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedCaseId(null);
          }}
          onSuccess={loadCases}
        />
      )}

      {/* Create Case Modal */}
      {showCreateCaseModal && (
        <ClientCreateCaseModal
          onClose={() => setShowCreateCaseModal(false)}
          onSuccess={() => {
            setShowCreateCaseModal(false);
            loadCases();
          }}
          user={user}
        />
      )}

      {/* Limited POA Form Modal */}
      {showPOAForm && selectedCaseForPOA && (
        <LimitedPOAForm
          isOpen={showPOAForm}
          onClose={() => {
            setShowPOAForm(false);
            setSelectedCaseForPOA(null);
          }}
          caseData={selectedCaseForPOA}
          onSubmitSuccess={handlePOASuccess}
        />
      )}
    </div>
  );
};

// Client Create Case Modal Component
const ClientCreateCaseModal = ({ onClose, onSuccess, user }) => {
  const [formData, setFormData] = useState({
    company_name: user?.company_name || '',
    frozen_amount: '',
    freeze_date: format(new Date(), 'yyyy-MM-dd'),
    processor_name: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const caseData = {
        client_id: user?.id || `client_${Date.now()}`,
        client_name: user?.full_name || 'Unknown',
        client_email: user?.email || '',
        company_name: formData.company_name,
        frozen_amount: parseFloat(formData.frozen_amount),
        freeze_date: new Date(formData.freeze_date).toISOString(),
        processor_name: formData.processor_name
      };

      await casesAPI.createClientCase(caseData);
      toast.success('Case created successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create case');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Create New Case</h2>
            <p className="text-sm text-slate-600 mt-1">Submit a new reserve recovery case</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Company Name *</label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              placeholder="Your business name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Processor Name *</label>
            <input
              type="text"
              name="processor_name"
              value={formData.processor_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              placeholder="e.g., Stripe, Square, PayPal"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Frozen Amount ($) *</label>
              <input
                type="number"
                name="frozen_amount"
                value={formData.frozen_amount}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Freeze Date *</label>
              <input
                type="date"
                name="freeze_date"
                value={formData.freeze_date}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                required
              />
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm">
            <p className="text-slate-700">
              <strong>Note:</strong> After creating your case, you will be able to upload supporting documents 
              such as merchant statements, bank documents, and correspondence with the processor.
            </p>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 primary-button py-3 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Case'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// POA List Component for Power of Attorney Tab
const POAList = ({ cases, casePOAStatus, onSignPOA, loadCases }) => {
  const [poaDocuments, setPOADocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPOADocuments = React.useCallback(async () => {
    setLoading(true);
    const docs = [];
    
    for (const caseItem of cases) {
      try {
        const response = await poaAPI.getPOAForCase(caseItem.id);
        if (response.data) {
          docs.push({
            ...response.data,
            case_name: caseItem.company_name,
            case_id: caseItem.id,
            caseItem: caseItem
          });
        }
      } catch (error) {
        // No POA for this case - add placeholder
        docs.push({
          id: null,
          status: 'not_started',
          case_name: caseItem.company_name,
          case_id: caseItem.id,
          caseItem: caseItem
        });
      }
    }
    
    setPOADocuments(docs);
    setLoading(false);
  }, [cases]);

  useEffect(() => {
    loadPOADocuments();
  }, [loadPOADocuments, casePOAStatus]);

  const handleDownloadPOA = async (poaId) => {
    try {
      const response = await poaAPI.downloadPDF(poaId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `LPOA_${poaId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('POA downloaded successfully');
    } catch (error) {
      toast.error('Failed to download POA');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">✓ Completed</span>;
      case 'pending_admin':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">⏳ Pending Admin Signature</span>;
      case 'draft':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">📝 Draft</span>;
      default:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">⚠ Not Started</span>;
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading POA documents...</div>;
  }

  const completedPOAs = poaDocuments.filter(p => p.status === 'completed');
  const pendingPOAs = poaDocuments.filter(p => p.status === 'pending_admin');
  const notStartedPOAs = poaDocuments.filter(p => p.status === 'not_started' || p.status === 'draft');

  return (
    <div className="space-y-6">
      {/* Completed POAs */}
      {completedPOAs.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-green-700 mb-4 flex items-center gap-2">
            <CheckCircle size={20} />
            Completed Power of Attorney ({completedPOAs.length})
          </h3>
          <div className="space-y-3">
            {completedPOAs.map((poa) => (
              <div key={poa.id} className="border border-green-200 bg-green-50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-slate-900">{poa.case_name}</h4>
                    <p className="text-sm text-slate-600">Limited Power of Attorney</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Signed: {poa.client_signature?.signed_at ? format(new Date(poa.client_signature.signed_at), 'MMM dd, yyyy') : 'N/A'}
                      {poa.admin_signature?.signed_at && ` • Counter-signed: ${format(new Date(poa.admin_signature.signed_at), 'MMM dd, yyyy')}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(poa.status)}
                    <button
                      onClick={() => handleDownloadPOA(poa.id)}
                      className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-1"
                    >
                      <FileText size={14} />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending POAs */}
      {pendingPOAs.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-amber-700 mb-4 flex items-center gap-2">
            <Clock size={20} />
            Pending Admin Signature ({pendingPOAs.length})
          </h3>
          <div className="space-y-3">
            {pendingPOAs.map((poa) => (
              <div key={poa.id} className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-slate-900">{poa.case_name}</h4>
                    <p className="text-sm text-slate-600">Limited Power of Attorney - Awaiting counter-signature</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Your signature: {poa.client_signature?.signed_at ? format(new Date(poa.client_signature.signed_at), 'MMM dd, yyyy') : 'N/A'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(poa.status)}
                    <button
                      onClick={() => onSignPOA(poa.caseItem)}
                      className="px-3 py-1 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700"
                    >
                      View POA
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Not Started POAs */}
      {notStartedPOAs.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
            <Scale size={20} />
            Requires Signature ({notStartedPOAs.length})
          </h3>
          <div className="space-y-3">
            {notStartedPOAs.map((poa) => (
              <div key={poa.case_id} className="border border-slate-200 bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-slate-900">{poa.case_name}</h4>
                    <p className="text-sm text-slate-600">Limited Power of Attorney required to proceed with case</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(poa.status)}
                    <button
                      onClick={() => onSignPOA(poa.caseItem)}
                      className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 flex items-center gap-1"
                    >
                      <Scale size={14} />
                      Sign POA
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {poaDocuments.length === 0 && (
        <div className="text-center py-12">
          <Scale className="mx-auto text-slate-400 mb-4" size={48} />
          <p className="text-slate-600">No Power of Attorney documents found</p>
          <p className="text-sm text-slate-400 mt-1">Create a case to get started with a POA</p>
        </div>
      )}
    </div>
  );
};

export default ClientDashboard;
